import PropTypes from 'prop-types';
import { useEffect, useState, useContext } from 'react';
import { useRouter } from 'next/router';

import Ad from '../../components/Ad/Ad';
import { AdSizes } from '../../components/Ad/AdSizes';
import ChefsHostsBlock from '../../components/ChefsHosts/ChefsHostsBlock';
import { CHEFS_HOSTS_QUERY } from '../../components/ChefsHosts/ChefsHostsQuery';
import { CHEFS_HOSTS_BY_IDS_QUERY } from '../../components/ChefsHosts/ChefsHostsByIdsQuery';

import AdobeAnalyticsDataLayer from '../../components/AdobeAnalytics/AdobeAnalyticsDataLayer';
import SeoYoast from '../../components/Seo/SeoYoast';

import { initializeApollo } from '../../utils/apolloClient';
import { useScrollRestoration, restoreScrollPos } from '../../utils/useScrollRestoration';
import AppContext from '../../context/AppContext';
import { US_CONTENT } from '../../utils/geoBlock';

const sorted = (stars) => {
	const names = [];

	stars.forEach((star) => {
		const {
			authorId,
			details,
			slug,
			title,
		} = star;

		const { authorShows, usersProfilePicture } = details;

		names.push({
			authorId,
			authorShows,
			details,
			slug,
			title,
			usersProfilePicture,
		});
	});

	const result = names.sort((a, b) => a.slug.localeCompare(b.slug));

	return result;
};

const insertAds = (chefsHosts) => {
	let adCount = 1;
	let pos = 3;
	const interval = 4;
	const totalStars = chefsHosts.length;

	while (pos <= totalStars) {
		const sizes = {
			'xs sm md lg': AdSizes.BIG_BOX,
			'xl xxl': (adCount < 3) ? AdSizes.LEADERBOARD : AdSizes.HIDDEN,
		};
		const chefsHostsAd = (
			<Ad
				key={`chefs-hosts-ad-${adCount}`}
				sizes={sizes}
				htmlId={`chefs-hosts-ad-${adCount}`}
				wrapperClass={`chefs-hosts-ad-${adCount}`}
				className={`chefs-hosts-ad-${adCount}-wrapper`}
				pos={`${adCount}`}
			/>
		);

		chefsHosts.splice(pos, 0, chefsHostsAd); // insert ad in every 4th position
		adCount += 1;
		pos += interval;
	}

	return chefsHosts;
};

export default function ChefsHosts({ id = '', stars = [], seo = {} }) {
	const [initialLoad, setInitialLoad] = useState(true);
	const router = useRouter();
	const { isUsTraffic } = useContext(AppContext);

	// Remove any authors which are deemed invalid
	const validateStars = (authors) => {
		// Filter out US authors from being viewd by US traffic
		if (isUsTraffic) {
			return authors.filter((author) => {
				const distributions = author.distributions.nodes;
				return distributions.length === 0
					|| distributions.some((item) => item.slug !== US_CONTENT);
			});
		}

		return authors;
	};

	const sortedStars = sorted(validateStars(stars));
	const starsWithAds = insertAds(sortedStars);

	useEffect(() => {
		if (initialLoad) setInitialLoad(false);
	}, [initialLoad]);

	useEffect(() => {
		// wait for the ads before preserving scroll state
		setTimeout(() => {
			restoreScrollPos(router.asPath);
		}, 2000);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialLoad]);

	// Restore last location
	useScrollRestoration(router);

	return (
		<>
			<SeoYoast seo={seo} />
			<Ad
				sizes={{
					'xs sm': AdSizes.MOBILE_LEADERBOARD,
					'md lg': AdSizes.LEADERBOARD,
					'xl xxl': AdSizes.LEADERBOARD_COMBO,
				}}
				htmlId="chefs-hosts-leaderboard-ad"
				wrapperClass="chefs-hosts-leaderboard-ad leaderboard-ad"
			/>
			<AdobeAnalyticsDataLayer
				viewName="chefs-hosts"
				viewType="archive"
			/>
			<ChefsHostsBlock fncStars={starsWithAds} />
		</>
	);
}

export const getServerSideProps = async () => {
	const apolloClient = initializeApollo();

	const { data } = await apolloClient.query({
		query: CHEFS_HOSTS_QUERY,
		variables: {
			id: 'chefs-hosts',
			idType: 'URI',
		},
		errorPolicy: 'all',
	});

	if (!data || !data?.page?.chefsHostsSettings?.chefHostName) {
		return { notFound: true };
	}

	const names = data?.page?.chefsHostsSettings?.chefHostName;

	const ids = names.map((star) => star?.authorId);

	const variables = {
		authorIds: ids,
	};

	const { data: { authors } } = await apolloClient.query({
		query: CHEFS_HOSTS_BY_IDS_QUERY,
		variables,
		skip: ids?.length < 1,
	});

	const { seo, databaseId } = data?.page;

	return {
		props: {
			id: databaseId,
			stars: authors?.nodes,
			seo,
		},
	};
};

ChefsHosts.propTypes = {
	id: PropTypes.number,
	stars: PropTypes.array,
	seo: PropTypes.object,
};
